import {
  getToken,
  getCompany,
} from "../../../../shared/data/storage/authStorage";
import {
  InvoiceDTO,
  InvoiceDataDTO,
  InvoicesResponseDTO,
  InvoiceQueryParamsDTO,
  InvoiceOperationResponseDTO,
} from "../models/InvoiceDTOs";
import { API_BASE_URL, CORE_API_URL } from "../../../../config/api";
import type { ApprovalAuditPayload } from "../../../../shared/utils/reviewWorkflow";

function withApprovalAudit(
  body: Record<string, unknown>,
  approvalAudit?: ApprovalAuditPayload,
): Record<string, unknown> {
  if (!approvalAudit) {
    return body;
  }
  return {
    ...body,
    approval_audit: {
      reason: approvalAudit.reason,
      change_details: approvalAudit.change_details,
    },
  };
}

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// HTTP Status Codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// Fetch invoices function
export const fetchInvoices = async (
  params: InvoiceQueryParamsDTO = {},
): Promise<InvoicesResponseDTO> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const queryParams = new URLSearchParams();

  if (params.limit !== undefined) {
    const limit = Math.min(params.limit, 100);
    queryParams.append("limit", limit.toString());
  }

  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }

  if (params.status) {
    queryParams.append("status", params.status.toString());
  }

  if (params.reviewed) {
    queryParams.append("reviewed", params.reviewed.toString());
  }

  if (params.search) {
    queryParams.append("search", params.search);
  }

  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }

  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }

  if (params.created_after) {
    queryParams.append("created_after", params.created_after);
  }

  if (params.created_before) {
    queryParams.append("created_before", params.created_before);
  }

  if (params.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }

  if (params.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }

  const url = `${API_BASE_URL}/api/invoices${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    // Return as is - mapper will handle parsing
    return data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Get a single invoice by ID
export const getInvoice = async (invoiceId: number): Promise<InvoiceDTO> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();

    // Return the invoice data as is (will be parsed by mapper)
    if (data.success && data.data) {
      return data.data;
    }

    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update invoice JSON data specifically
export const updateInvoiceJson = async (
  invoiceId: number,
  invoiceJson: InvoiceDataDTO,
): Promise<InvoiceDTO> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/update-invoice-json`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoice_id: invoiceId,
          invoice_json: invoiceJson,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData,
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update invoice status
export const updateInvoiceStatus = async (
  invoiceId: number,
  status: number,
) => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(`${API_BASE_URL}/api/invoices/update-status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        id: invoiceId,
        status: status,
      }),
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to update invoice status: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating invoice status:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Post invoice to the company's accounting backend (Zoho or Tally).
export const createZohoInvoice = async (
  invoiceData: InvoiceDataDTO,
  documentId: string,
  pdfUrl: string,
  approvalAudit?: ApprovalAuditPayload,
) => {
  try {
    const token = getToken();
    const companyData = getCompany();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/post-to-accounting`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          withApprovalAudit(
            {
              invoice_id: parseInt(documentId),
              invoiceJson: invoiceData,
              company_gst_enabled: companyData?.companyGstEnabled,
              pdfUrl: pdfUrl,
            },
            approvalAudit,
          ),
        ),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to create Zoho invoice: ${response.statusText}`,
        response.status,
      );
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("Error creating Zoho invoice:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Update existing Zoho invoice
export const updateZohoInvoice = async (
  documentId: string,
  invoiceData: InvoiceDataDTO,
  approvalAudit?: ApprovalAuditPayload,
) => {
  try {
    const token = getToken();
    const companyData = getCompany();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/update-in-accounting`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          withApprovalAudit(
            {
              invoice_id: parseInt(documentId),
              invoiceJson: invoiceData,
              company_gst_enabled: companyData?.companyGstEnabled,
            },
            approvalAudit,
          ),
        ),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new APIError(
        errorData.message ||
          `Failed to update Zoho invoice: ${response.statusText}`,
        response.status,
      );
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error("Error updating Zoho invoice:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Mark invoice as duplicate
export const markInvoiceAsDuplicate = async (
  invoiceId: number,
): Promise<any> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/mark-as-duplicate`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: invoiceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message ||
          `Failed to mark invoice as duplicate: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error marking invoice as duplicate:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Reject invoice
export const rejectInvoice = async (invoiceId: number): Promise<any> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const response = await fetch(
      `${API_BASE_URL}/api/invoices/reject-invoice`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: invoiceId,
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `Failed to reject invoice: ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error rejecting invoice:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Delete posted invoice (from Zoho and soft-delete locally)
 */
export const deletePostedInvoice = async (
  invoiceId: number,
): Promise<InvoiceOperationResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to delete invoice: ${response.statusText}`,
      response.status,
    );
  }
  return data;
};

/**
 * Options for write-off when invoice may not exist locally (e.g. Zoho-only).
 * Send zoho_invoice_id in body so backend can fetch from Zoho and insert, then write off.
 */
export interface WriteOffInvoiceOptions {
  zohoInvoiceId?: string;
}

/**
 * Write off an invoice in Zoho Books
 * POST /api/invoices/{id}/writeoff
 * {id} can be local invoice id (number) or Zoho invoice id (string) when invoice is not in our table.
 */
export const writeOffInvoice = async (
  invoiceId: number | string,
): Promise<InvoiceOperationResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/${String(invoiceId)}/writeoff`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to write off invoice",
      response.status,
    );
  }
  return data;
};

/**
 * Cancel write off for an invoice in Zoho Books
 * POST /api/invoices/{id}/writeoff/cancel
 * When invoice is not in our table, pass id=0 and options.zohoInvoiceId in body (if backend supports it).
 */
export const cancelWriteOffInvoice = async (
  invoiceId: number,
): Promise<InvoiceOperationResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/${invoiceId}/writeoff/cancel`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || "Failed to cancel write off for invoice",
      response.status,
    );
  }
  return data;
};

// Fetch invoice counts for dashboard cards
export const fetchInvoiceCounts = async (params?: {
  date_from?: string;
  date_to?: string;
}): Promise<{
  pending_count: number;
  on_hold_count: number;
  under_review_count: number;
  posted_count: number;
  duplicate_count: number;
  rejected_count: number;
  rejected_duplicate_count: number;
}> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED,
      );
    }

    const queryParams = new URLSearchParams();
    if (params?.date_from) {
      queryParams.append("date_from", params.date_from);
    }
    if (params?.date_to) {
      queryParams.append("date_to", params.date_to);
    }

    const url = `${API_BASE_URL}/api/invoices/counts${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
      );
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching invoice counts:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

// Process invoice JSON for GL classification
export const processInvoiceJson = async (invoiceData: InvoiceDataDTO) => {
  try {
    const response = await fetch(
      `${CORE_API_URL}/gl_classification_interface.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "process_invoice_json",
          invoice_json: invoiceData,
        }),
      },
    );

    if (response.ok) {
      return {
        success: true,
      };
    }
  } catch (error) {
    console.error("Error processing invoice JSON:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Create manual invoice (multipart: invoice_json, company_gst_enabled, optional attachments[]).
 * POST /api/invoices/manual
 */
export const createManualInvoice = async (
  invoiceJson: InvoiceDataDTO | Record<string, unknown>,
  companyGstEnabled: number,
  attachments?: File[],
): Promise<{
  success: boolean;
  data?: { id: number; zoho_invoice_id?: string; invoice_number?: string };
  message?: string;
}> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }

  const formData = new FormData();
  formData.append("invoice_json", JSON.stringify(invoiceJson));
  formData.append("company_gst_enabled", String(companyGstEnabled));
  if (attachments?.length) {
    attachments.forEach((file) => formData.append("attachments[]", file));
  }

  const response = await fetch(`${API_BASE_URL}/api/invoices/manual`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to create manual invoice: ${response.statusText}`,
      response.status,
      data,
    );
  }
  return data;
};

/**
 * Create manual invoice draft only (insert + S3, no push to Zoho). For review flow.
 * POST /api/invoices/manual with draft: true (JSON) or draft=1 (multipart).
 */
export const createManualInvoiceDraft = async (
  invoiceJson: InvoiceDataDTO | Record<string, unknown>,
  companyGstEnabled: number,
  attachments?: File[],
): Promise<{ success: boolean; data?: { id: number }; message?: string }> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  if (attachments?.length) {
    const formData = new FormData();
    formData.append("invoice_json", JSON.stringify(invoiceJson));
    formData.append("company_gst_enabled", String(companyGstEnabled));
    formData.append("draft", "1");
    attachments.forEach((file) => formData.append("attachments[]", file));
    const response = await fetch(`${API_BASE_URL}/api/invoices/manual`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message ||
          `Failed to create invoice draft: ${response.statusText}`,
        response.status,
        data,
      );
    }
    return data;
  }
  const response = await fetch(`${API_BASE_URL}/api/invoices/manual`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      invoice_json: invoiceJson,
      company_gst_enabled: companyGstEnabled,
      draft: true,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to create invoice draft: ${response.statusText}`,
      response.status,
      data,
    );
  }
  return data;
};

/**
 * Push an existing manual invoice draft to Zoho (from review page or approval).
 * POST /api/invoices/manual with JSON body: { invoice_id, company_gst_enabled [, invoice_json ] }.
 * When invoice_json is provided (edited form/approval data), backend uses it for the push and persists to the draft row.
 */
export const pushManualInvoiceDraft = async (
  invoiceId: number,
  companyGstEnabled: number,
  invoiceJson?: InvoiceDataDTO | Record<string, unknown> | null,
  approvalAudit?: ApprovalAuditPayload,
): Promise<{
  success: boolean;
  data?: { id: number; zoho_invoice_id?: string; invoice_number?: string };
  message?: string;
}> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED,
    );
  }
  const body: Record<string, unknown> = {
    invoice_id: invoiceId,
    company_gst_enabled: companyGstEnabled,
  };
  if (invoiceJson != null && typeof invoiceJson === "object") {
    body.invoice_json = invoiceJson;
  }
  const requestBody = withApprovalAudit(body, approvalAudit);
  const response = await fetch(`${API_BASE_URL}/api/invoices/manual`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestBody),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      data.message || `Failed to push manual invoice: ${response.statusText}`,
      response.status,
      data,
    );
  }
  return data;
};

export interface RerunCustomerChangeResult {
  success: boolean;
  message?: string;
  data?: { invoice_id: number; audit_id: number | null; status: string };
}

export interface RecomputeStatusResult {
  status: "idle" | "recomputing" | "done" | "error" | "unknown";
  change_diff?: unknown[];
  lines_changed?: number;
  message?: string;
}

/**
 * Trigger the customer-change pipeline rerun (GL + supply/tax).
 * Persists the chosen customer and kicks the cron; poll getRecomputeStatus for completion.
 */
export const rerunCustomerChange = async (
  invoiceId: string | number,
  newCustomerId: string,
  newCustomerName: string,
): Promise<RerunCustomerChangeResult> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/rerun-customer-change`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoice_id: invoiceId,
          new_customer_id: newCustomerId,
          new_customer_name: newCustomerName,
        }),
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data,
      );
    }
    return data as RerunCustomerChangeResult;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

/**
 * Poll the rerun status for an invoice. The first call that observes the GL cron
 * done (stage 40) finalises the rerun server-side.
 */
export const getRecomputeStatus = async (
  invoiceId: string | number,
): Promise<RecomputeStatusResult> => {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/invoices/recompute-status/${invoiceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new APIError(
        data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data,
      );
    }
    return (data.data ?? { status: "unknown" }) as RecomputeStatusResult;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR,
    );
  }
};

export const invoicesAPI = {
  fetchInvoices,
  getInvoice,
  updateInvoiceJson,
  updateInvoiceStatus,
  createZohoInvoice,
  updateZohoInvoice,
  createManualInvoice,
  createManualInvoiceDraft,
  pushManualInvoiceDraft,
  markInvoiceAsDuplicate,
  rejectInvoice,
  fetchInvoiceCounts,
  processInvoiceJson,
};
